// Catalog.ID Client — Booklet Account Recovery Sheet Page
// ============================================================================
// One recovery sheet per printed page (registration-restructure §5; Addendum
// §17 — the print-only booklet creates no durable quorum file, and
// physical page separation carries the security). Redesigned from the A5 card:
// QR much bigger, one self-contained explanation. Dropped as noise (decided
// 2026-09-01): the recovery-set id/hash and the bundle fingerprint row. The
// card checksum went with them and came BACK in round 4 (2026-09-03) as the
// verification line's fingerprint — it earns its ink now that it sits next to
// the address of a page that checks it. Round 6 (2026-09-04, §13.2) widened it
// to 16 characters and moved it into a symbol column of its own — a vertical
// Code 128 plus its printed text — and round 7 (2026-09-07, §14.1) took that
// column out again: those 8 bytes are the first 8 bytes of the MAC that is
// already inside the QR and that `decodeRecoveryCard` verifies, so the barcode
// could catch nothing the payload's own integrity check misses. The 16
// characters are back on the verification line, in plain text, for a custodian
// who wants to confirm by eye that the page checked the sheet in their hand.
//
// Review 2026-09-02 (second round). The page is written for the person HOLDING
// the sheet, and it deliberately carries NO map: printing the other sheets'
// labels would hand every custodian a route to a quorum. Instead each sheet has
// hand-fill lines — an emergency contact and a whereabouts line — so the
// account holder can write the coordinator's name on every sheet and the full
// whereabouts only on the coordinator's own (Addendum §9.6).
//
// Everything else on the page is budget for the QR, which now fits 340 pt on a
// page that must never spill onto a second sheet:
//   - the sheet type and its position moved into the page title;
//   - the `Created` row is gone — it always equalled the header's Version
//     stamp (a kit is printed in the minute it is created, and a reissue is a
//     NEW kit, never a reprint of this one);
//   - copies left the page body entirely (third round): the master set holds
//     every sheet type ONCE, and each additional copy is a separate sheet printed
//     after the set, identified by its own footer line rather than a `Copy` row.
// The remaining sheet rows continue the account-header table (no gap after
// Version). No "keep separate" line: the booklet itself holds the supersecret.
//
// PAGE BUDGET — this page has none to spare. Measured on A4 (rasterised sample
// render): the 3-of-5 ORGANIZATION sheet as the presets ship it used to clear
// the footer by ~20 pt. Round 4 (2026-09-03) spent that and more — an offline
// key-backup sentence and the verification line — and bought the room by
// shrinking the QR from 340 pt to QR_FIT below. (Round 5 removed the per-sheet
// consent line again, handing a line of slack back to the budget.) That is why the title margin, the header table's bottom
// margin, the QR and paragraph margins and the fill-line padding are all set
// here rather than left to the shared styles. Anything added to this page must
// buy its own room, and the sample renderer (`apps/cid/dev/render-booklet.ts`)
// must be re-run to prove it: a sheet that spills prints as TWO pages, breaks
// the one-sheet-per-page contract AND shifts every page number after it.

import type { Content, TableCell, TableLayout } from 'pdfmake/interfaces';
import { qrToSvg } from '../qr-svg';
import { cardChecksum, encodeRecoveryCard, type RecoveryCardPayload } from '../../crypto/recovery-card';
import {
    accountHeader, type AccountHeaderData, cardTypeLetter, cardTypeColor, recoverySheetCopyTitle,
    recoverySheetTitle,
} from './shared';

/** Ruled blank rows to write on — a dotted rule under each row, nothing
 *  printed. Dotted rather than solid: the line reads as "write here", and a
 *  pen stroke along it stays distinguishable from the print. */
const FILL_LINE_LAYOUT: TableLayout = {
    hLineWidth: (i: number) => (i === 0 ? 0 : 0.6),
    vLineWidth: () => 0,
    hLineColor: () => '#000000',
    hLineStyle: () => ({ dash: { length: 1, space: 2 } }),
    paddingTop: () => 5,
    paddingBottom: () => 0,
    paddingLeft: () => 0,
    paddingRight: () => 0,
};

export interface RecoveryCardPageData {
    header: AccountHeaderData;
    payload: RecoveryCardPayload;
    /** The account's verified contact channels, so a custodian can tell whose
     *  sheet this is (decided 2026‑09‑02). One header row, both values. */
    contact?: RecoveryCardContact;
    /** Where the holder can check the sheet is intact, e.g. 'b4np.catalog.id'
     *  — passed exactly as `reset-card-page.ts` receives it. Absent falls back
     *  to naming the home node rather than a host. */
    nodeHost?: string;
    /**
     * A COPY handed to a custodian rather than the original that stays in the
     * printed set (staging test 2026-10-15). The custodian — a friend, a notary —
     * needs to read on the sheet why they have it and what to do with it, so the
     * copy trades the owner's paragraphs for theirs: the technical one about an
     * offline key backup, the note about copies counting once, and the "Where the
     * other sheets are" line, which a custodian has no business reading. Traded,
     * not added — the page has no room to spare (PAGE BUDGET above).
     */
    copy?: boolean;
}

/** Per-sheet options shared by the master pages and their copies. */
export interface RecoveryCardPageOptions {
    nodeHost?: string;
}

export interface RecoveryCardContact {
    email?: string;
    phone?: string;
}

/** QR edge; the letter column takes the rest of the 475 pt content width.
 *  340 pt before round 4 — see the PAGE BUDGET note above. At 285 pt a
 *  ~214-byte payload (version 15-17, ECC H) still prints ~1.2 mm per module,
 *  twice the Addendum §6.2 archival minimum. Neither round 6 nor round 7
 *  touched it: the barcode column was bought out of the letter's width, and
 *  giving it back cost the QR nothing either. */
const QR_FIT = 285;

/** 110 pt while the letter shared its column with the Code 128 and its printed
 *  characters (round 6); the whole column is the letter's again since round 7
 *  (§14.1). It is the largest thing on the page by far, and what tells A from B
 *  across a table of sheets — the one glance the page is designed around. */
const LETTER_FONT_SIZE = 130;

const COLUMN_GAP = 10;

/** Kit shape {@link buildRecoveryCardPages} needs — structurally a RecoveryKit. */
export interface RecoveryCardPagesKit {
    cards: { payload: RecoveryCardPayload; extraCopies?: number }[];
}

/** One extra sheet printed after the master set: the sheet page verbatim plus
 *  the footer line that tells the holder which copy they are looking at. Copy
 *  sheets are NOT numbered into "page n of n" — the master set owns that. */
export interface RecoveryCardCopySheet {
    content: Content[];
    footer: string;
}

/** "sheet B" / "any one of sheets B or C" / "any 2 of sheets B, C and D" /
 *  "sheets B, C and D" — the sheets a holder must bring together with this one.
 *  (Identifier kept: the code word for a share type is still `card`.) */
export function describeOtherCards(letters: string[], needed: number): string {
    const list = letters.length <= 1
        ? letters.join('')
        : `${letters.slice(0, -1).join(', ')} ${needed >= letters.length ? 'and' : 'or'} ${letters[letters.length - 1]}`;
    if (letters.length === 1) return `sheet ${list}`;
    if (needed >= letters.length) return `sheets ${letters.slice(0, -1).join(', ')} and ${letters[letters.length - 1]}`;
    return needed === 1 ? `any one of sheets ${list}` : `any ${needed} of sheets ${list}`;
}

/** A blank ruled block of `rows` lines, introduced by a bold caption. */
function fillInBlock(caption: string, rows: number, topMargin: number): Content[] {
    return [
        { text: caption, style: 'note', bold: true, margin: [0, topMargin, 0, 0] },
        {
            layout: FILL_LINE_LAYOUT,
            table: {
                widths: ['*'],
                body: Array.from({ length: rows }, () => [{ text: ' ', style: 'note' }] as TableCell[]),
            },
        },
    ];
}

export function buildRecoveryCardPage(data: RecoveryCardPageData): Content[] {
    const p = data.payload;
    const letter = cardTypeLetter(p.shareIndex);
    const otherLetters = Array.from({ length: p.totalSharesN }, (_, i) => i + 1)
        .filter(i => i !== p.shareIndex)
        .map(cardTypeLetter);
    const needed = p.thresholdK - 1;
    const encoded = encodeRecoveryCard(p);
    const verifyAt = data.nodeHost
        ? `${data.nodeHost}/recovery`
        : 'your Catalog.ID home node, page /recovery';

    const cardRows: TableCell[][] = [];
    const contact = [data.contact?.email, data.contact?.phone].filter((v): v is string => Boolean(v && v.trim()));
    if (contact.length > 0) cardRows.push([{ text: 'Contact', style: 'label' }, { text: contact.join('   ·   '), style: 'value' }]);
    if (p.cardLabel) cardRows.push([{ text: 'Purpose', style: 'label' }, { text: p.cardLabel, style: 'value' }]);

    return [
        {
            // Just the letter — no "(n of n)" (round 5, 2026-09-03): the body
            // copy right below states "one of N", so the suffix was noise.
            text: recoverySheetTitle(p.shareIndex),
            style: 'pageTitle',
            margin: [0, 0, 0, 6],
        },
        accountHeader(data.header, cardRows, 8),
        {
            // QR left, the sheet letter huge and in the type colour on the right
            // — the one glance that tells A from B across a table of sheets.
            // Round 6 put a vertical Code 128 of the payload's fingerprint
            // between them; round 7 removed it (§14.1) — a second symbol that
            // repeated 8 bytes of the QR's own MAC proved nothing the MAC does
            // not already prove, and nothing takes its place: a small QR there
            // would be the same redundancy under another name.
            columns: [
                { width: QR_FIT, svg: qrToSvg(encoded), fit: [QR_FIT, QR_FIT] },
                {
                    width: '*',
                    text: letter,
                    fontSize: LETTER_FONT_SIZE,
                    bold: true,
                    color: cardTypeColor(p.shareIndex),
                    alignment: 'center',
                    margin: [0, Math.round((QR_FIT - LETTER_FONT_SIZE) / 2) - 10, 0, 0],
                },
            ],
            columnGap: COLUMN_GAP,
            margin: [0, 0, 0, 4],
        },
        // Verification line (round 4; the fingerprint left it in round 6 for the
        // barcode column and came back in round 7 when that column went). The
        // fingerprint is the first 8 MAC bytes of the very payload in the QR
        // above, so a holder can read it back off a page that may have been in
        // a drawer for a decade and see that the verification page is
        // describing the sheet in their hand — printed once, here.
        // The address comes LAST, with nothing after it (staging test
        // 2026-10-16: "… at <address> — fingerprint …" read as if the
        // fingerprint were part of the address).
        {
            text: `Fingerprint ${cardChecksum(encoded)} — check this sheet at ${verifyAt}`,
            style: 'note',
            bold: true,
            margin: [0, 0, 0, 6],
        },
        ...(data.copy
            ? custodianText(p, letter, otherLetters, needed, verifyAt)
            : ownerText(p, letter, otherLetters, needed)),
    ];
}

/** The original's closing paragraphs — for the account's owner, who keeps it in
 *  the printed set. */
function ownerText(p: RecoveryCardPayload, letter: string, otherLetters: string[], needed: number): Content[] {
    return [
        {
            text: `This sheet is one of ${p.totalSharesN} for the account above. Everything in Catalog is `
                + 'encrypted; these sheets exist for an emergency in which the holder no longer has the keys '
                + 'or has died. '
                + `Any ${p.thresholdK} of the ${p.totalSharesN} together recreate the supersecret that `
                + 'unlocks the account and everything in it. They also recover the decryption keys from an '
                + 'offline encrypted key-backup file (USB stick, cloud or custody) when no Catalog.ID node '
                + 'is reachable. This sheet alone reveals nothing.',
            style: 'value',
            margin: [0, 0, 0, 6],
        },
        {
            unbreakable: true,
            stack: [
                { text: 'Handling', style: 'section', margin: [0, 0, 0, 3] },
                {
                    ul: [
                        `You hold sheet ${letter}. To unlock the account, bring it together with `
                            + `${describeOtherCards(otherLetters, needed)}, open Catalog.ID Recovery on a `
                            + 'trusted device, and scan the sheets.',
                        'Multiple printed copies of the same sheet type count as ONE type.',
                    ],
                    style: 'note',
                },
                ...fillInBlock('In an emergency, contact:', 1, 3),
                ...fillInBlock('Where the other sheets are:', 1, 4),
            ],
        },
    ];
}

/** A copy's closing paragraphs — for the custodian it is handed to. Plain
 *  words: this reader may never have heard of Catalog.ID.
 *
 *  Staging test 2026-10-16: not "where you would keep a passport" — that is
 *  where it is easy to grab for a trip — but with the holder's other
 *  confidential papers. And no "you may be asked once a year": the owner tests
 *  the kit with the originals in the printed set, so nobody asks a custodian;
 *  what a custodian can do is check their own sheet, at the address in full. */
function custodianText(
    p: RecoveryCardPayload, letter: string, otherLetters: string[], needed: number, verifyAt: string,
): Content[] {
    return [
        {
            text: 'You hold this sheet for the account above, so that the account can be recovered if its '
                + `own credentials are lost or its holder can no longer act. It is one of ${p.totalSharesN} `
                + `recovery sheets; any ${p.thresholdK} of them together recover the account. On its own `
                + 'this sheet reveals nothing and cannot be used.',
            style: 'value',
            margin: [0, 0, 0, 6],
        },
        {
            unbreakable: true,
            stack: [
                { text: 'For the person holding this sheet', style: 'section', margin: [0, 0, 0, 3] },
                {
                    ul: [
                        'Keep it in a safe place with your other confidential papers. Do not make copies of '
                            + 'it, on paper or digitally.',
                        'Hand it over only to the account holder or, if they cannot act, to the person named '
                            + 'below.',
                        `Recovery brings this sheet ${letter} together with ${describeOtherCards(otherLetters, needed)}`
                            + ', scanned in Catalog.ID Recovery on a trusted device.',
                        `To check that it is still the current sheet, open ${verifyAt} and scan its QR code.`,
                    ],
                    style: 'note',
                },
                ...fillInBlock('In an emergency, contact:', 1, 3),
            ],
        },
    ];
}

/** The MASTER pages: every sheet type of a kit exactly once, in share order.
 *  This is what "page n of n" counts, and it leaves the owner a
 *  first-generation original of each type. The single entry point for the
 *  wizards, the Settings creator and the org kit pane, so a sheet reissued
 *  later is indistinguishable from one printed at registration. */
export function buildRecoveryCardPages(
    header: AccountHeaderData, kit: RecoveryCardPagesKit, contact?: RecoveryCardContact,
    options: RecoveryCardPageOptions = {},
): Content[][] {
    return kit.cards.map(card => buildRecoveryCardPage({ header, payload: card.payload, contact, ...options }));
}

/** The ADDITIONAL copies, printed after the master set — one sheet per extra
 *  copy, content identical to the master sheet page (identical share, identical
 *  QR), each carrying its own footer instead of a page number. */
export function buildRecoveryCardCopySheets(
    header: AccountHeaderData, kit: RecoveryCardPagesKit, contact?: RecoveryCardContact,
    options: RecoveryCardPageOptions = {},
): RecoveryCardCopySheet[] {
    const sheets: RecoveryCardCopySheet[] = [];
    for (const card of kit.cards) {
        const extra = card.extraCopies ?? 0;
        for (let index = 1; index <= extra; index++) {
            sheets.push({
                content: buildRecoveryCardPage({ header, payload: card.payload, contact, ...options, copy: true }),
                // The same string the introduction's contents list prints for
                // this copy (kit-layout.ts `recoveryCopyTitles`).
                footer: recoverySheetCopyTitle(card.payload.shareIndex, index, extra),
            });
        }
    }
    return sheets;
}
